import type { ResultOf } from '@graphql-typed-document-node/core';
import type { CombinedError } from '@urql/core';
import { useEffect, useMemo, useState } from 'react';
import { useClient } from 'urql';
import { pipe, subscribe } from 'wonka';

import {
  TAXONOMY_TREE_QUERY,
  type TaxonomyTreeQuery,
  TaxonomyType,
} from '@revelio/data-access';
import type { TreeData } from '@revelio/ui';

import {
  getTaxonomyBranchLabels,
  getTaxonomyCacheKey,
  normalizeTaxonomyTree,
  type TaxonomyLevelMetadata,
} from '../utils/taxonomyTree';

export type UseTaxonomyTreeResult = {
  cacheKey: string | null;
  taxonomy: TaxonomyTreeQuery['taxonomy'] | undefined;
  treeData: TreeData[];
  levels: TaxonomyLevelMetadata[];
  branchLabels: ReturnType<typeof getTaxonomyBranchLabels>;
  fetching: boolean;
  error: CombinedError | undefined;
};

type TaxonomyTreeQueryState = {
  /** `cacheKey` of the operation the result belongs to (null before any result). */
  key: string | null;
  data: ResultOf<typeof TAXONOMY_TREE_QUERY> | undefined;
  error: CombinedError | undefined;
};

// Successful results shared across hook instances, keyed by `cacheKey`. urql's
// `useQuery` used to hand a cached tree to a freshly-mounted hook on its FIRST
// render; the effect-based subscription below can't (state lands a paint
// later), and consumers that build request variables from the tree without a
// readiness gate (e.g. the treemap drilldown's composition query) fired that
// first render with no tree — SkillLvl_1 breakdowns instead of the taxonomy's
// first tier. This render-readable cache restores the synchronous path without
// reintroducing render-phase setState. Bounded by the handful of
// taxonomyType:id:version:levels combinations a session touches.
const taxonomyTreeResultCache = new Map<string, TaxonomyTreeQueryState>();

export const useTaxonomyTree = ({
  taxonomyType,
  taxonomyId,
  taxonomyVersion,
  levels,
  pause = false,
  priority = 'low',
  includeTitles: includeTitlesOverride,
}: {
  taxonomyType: TaxonomyType;
  taxonomyId?: number;
  taxonomyVersion?: string;
  levels?: number[];
  pause?: boolean;
  includeTitles?: boolean;
  /**
   * urql `priorityExchange` hint. Trees default to `low` so they yield to the
   * page's data queries. Pass `high` for a tree that IS on a data query's
   * critical path (e.g. the RICS tree the adaptive-taxonomy resolution — and
   * therefore the composition query — waits on), so it isn't queued behind the
   * filter-UI picker trees.
   */
  priority?: 'high' | 'low';
}): UseTaxonomyTreeResult => {
  const levelsKey = levels ? [...levels].sort((a, b) => a - b).join(',') : '';
  // biome-ignore lint/correctness/useExhaustiveDependencies: depend on sorted value key so callers may pass inline arrays safely
  const normalizedLevels = useMemo(
    () => (levels ? [...levels].sort((a, b) => a - b) : undefined),
    [levelsKey]
  );
  const includeTitles =
    includeTitlesOverride ??
    (taxonomyType === TaxonomyType.Role || taxonomyType === TaxonomyType.Skill);
  const variables = useMemo(
    () => ({
      taxonomyType,
      taxonomyId: taxonomyId ?? 0,
      taxonomyVersion,
      levels: normalizedLevels,
      includeTitles,
    }),
    [includeTitles, normalizedLevels, taxonomyId, taxonomyType, taxonomyVersion]
  );
  const cacheKey =
    taxonomyId === undefined
      ? null
      : getTaxonomyCacheKey({
          taxonomyType,
          taxonomyId,
          taxonomyVersion,
          levels: normalizedLevels,
          includeTitles: includeTitlesOverride,
        });

  const client = useClient();
  const isPaused = pause || taxonomyId === undefined || cacheKey === null;
  const [queryResult, setQueryResult] = useState<TaxonomyTreeQueryState>({
    key: null,
    data: undefined,
    error: undefined,
  });

  // The tree is subscribed manually (client.query in an effect) instead of
  // through urql's `useQuery`. `useQuery` synchronises its state to a new
  // source with a render-phase setState; interleaved with the primary-filter
  // commit's update cascade those render-phase updates can self-sustain — each
  // commit re-arms the next one and the renderer freezes without ever yielding
  // (90s Playwright timeouts on the Update click). Keeping every state write
  // inside an effect/subscription callback makes that loop impossible, while
  // the client's document cache still dedupes repeat subscriptions per
  // operation key, so request behaviour is unchanged.
  useEffect(() => {
    if (isPaused || cacheKey === null) {
      return;
    }
    const subscription = pipe(
      client.query(TAXONOMY_TREE_QUERY, variables, {
        requestPolicy: 'cache-first',
        priority,
        skipGlobalErrorHandling: true,
      }),
      subscribe((result) => {
        const next: TaxonomyTreeQueryState = {
          key: cacheKey,
          data: result.data,
          error: result.error,
        };
        // Only successful results are shared: the document cache doesn't cache
        // errors, so a remount after an error should look "fetching" while the
        // subscription retries, not settled-with-a-stale-error.
        if (result.data !== undefined && result.error === undefined) {
          taxonomyTreeResultCache.set(cacheKey, next);
        }
        setQueryResult(next);
      })
    );
    return () => subscription.unsubscribe();
  }, [client, variables, isPaused, priority, cacheKey]);

  // `fetching` is derived in render, not stored: an unpaused hook that has no
  // result for the CURRENT operation is fetching, even on the render where the
  // pause gate just opened and the subscription effect hasn't run yet. A stored
  // flag flips true one effect-cycle late, and that one-render "settled but
  // empty" window let composition requests fire with the SkillLvl_1 fallback
  // before the skill tree resolved (chart-builder / page-capabilities request
  // snapshots).
  const cachedResult =
    cacheKey === null ? undefined : taxonomyTreeResultCache.get(cacheKey);
  const settledResult =
    queryResult.key === cacheKey ? queryResult : cachedResult;
  const fetching = !isPaused && settledResult === undefined;
  // While a new operation is in flight, keep returning the previous result's
  // data (urql's `useQuery` behaviour) — consumers gate on `fetching`.
  const data = settledResult?.data ?? queryResult.data;
  const error = settledResult === undefined ? undefined : settledResult.error;

  const taxonomyPayload = data?.taxonomy;
  const normalizedTree = useMemo(
    () => normalizeTaxonomyTree(taxonomyPayload),
    [taxonomyPayload]
  );

  const branchLabels = useMemo(
    () => getTaxonomyBranchLabels(normalizedTree.levels),
    [normalizedTree.levels]
  );

  return {
    cacheKey,
    taxonomy: taxonomyPayload,
    treeData: normalizedTree.nodes,
    levels: normalizedTree.levels,
    branchLabels,
    fetching,
    error,
  };
};
